import ActionBarItem from '@rio-cloud/rio-uikit/components/actionBarItem/ActionBarItem';
import ReleaseNotesDialog from '@rio-cloud/rio-uikit/ReleaseNotesDialog';
import { Component, type JSX } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';

import { getLanguageData, getLocale, type LanguageDataInterface } from '../../configuration';
import type { State } from '../../reducers';
import { GlossaryDialog } from './GlossaryDialog';
import { isNewVersion, RELEASE_NOTES_ITEM } from './lastUsedVersion';
import { releaseNotes } from './releaseNotes';
import {
    getCurrentVersion,
    getShowGlossaryDialog,
    getShowReleaseNotes,
    serviceInformationActions,
} from './serviceInformation.redux';

const getLanguageForLinksInReleaseNotes = (locale: string): string => {
    if (locale === 'de-DE') {
        return 'de';
    }
    return 'en';
};

type ReleaseNotesProps = ReleaseNotesStateProps & ReleaseNotesDispatchProps;

interface ReleaseNotesStateProps {
    showReleaseNotes: boolean;
    translations: LanguageDataInterface;
    locale: string;
    showGlossaryDialog: boolean;
    currentVersion: string;
}

interface ReleaseNotesDispatchProps {
    setShowReleaseNotes: (payload: boolean) => void;
    toggleGlossaryDialog: () => void;
}

export class ReleaseNotes extends Component<ReleaseNotesProps> {
    getPopoverContent(hasNewReleaseNotes: boolean): JSX.Element {
        return (
            <div>
                <div className={'line-height-largest'}>
                    {/* eslint-disable-next-line */}
                    <a className={'ShowReleaseNotesLink'} onClick={() => this.props.setShowReleaseNotes(true)}>
                        <FormattedMessage id={'intl-msg:timed.glossary.whatsNew'} />
                        {hasNewReleaseNotes && <span className={'badge bg-primary margin-left-5'}>{'1'}</span>}
                    </a>
                </div>
                <div className={'line-height-largest'}>
                    {/* eslint-disable-next-line */}
                    <a
                        href={'#'}
                        className={'ShowGlossaryLink'}
                        onClick={(): void => this.props.toggleGlossaryDialog()}
                    >
                        <FormattedMessage id={'intl-msg:timed.glossary.help'} />
                    </a>
                </div>
            </div>
        );
    }

    render(): JSX.Element {
        const { showReleaseNotes, translations, locale } = this.props;

        const hasNewReleaseNotes = isNewVersion(this.props.currentVersion);
        const actionBarItemTitle = `${this.props.translations['intl-msg:timed.common.module.name']} v.${this.props.currentVersion}`;

        return (
            <ActionBarItem id={'serviceInfo'}>
                <ActionBarItem.Icon>
                    <span className={'icon rioglyph rioglyph-info-sign'} />
                    {hasNewReleaseNotes && <span className={'badge bg-primary'}>{'1'}</span>}
                </ActionBarItem.Icon>
                <ActionBarItem.Popover title={actionBarItemTitle}>
                    {this.getPopoverContent(hasNewReleaseNotes)}
                </ActionBarItem.Popover>
                <ReleaseNotesDialog
                    showReleaseNotes={showReleaseNotes}
                    setShowReleaseNotes={this.props.setShowReleaseNotes}
                    moduleName={translations['intl-msg:timed.common.module.name']}
                    whatsNewTitle={translations['intl-msg:timed.glossary.whatsNew']}
                    closeButtonText={translations['intl-msg:timed.common.close']}
                    translatedReleaseNotes={releaseNotes(
                        this.props.toggleGlossaryDialog,
                        getLanguageForLinksInReleaseNotes(locale)
                    )}
                    currentVersion={this.props.currentVersion}
                    RELEASE_NOTES_ITEM={RELEASE_NOTES_ITEM}
                />
                <GlossaryDialog show={this.props.showGlossaryDialog} toggleDialog={this.props.toggleGlossaryDialog} />
            </ActionBarItem>
        );
    }
}

const mapStateToProps = (state: State): ReleaseNotesStateProps => ({
    showReleaseNotes: getShowReleaseNotes(state),
    translations: getLanguageData(state),
    locale: getLocale(state),
    showGlossaryDialog: getShowGlossaryDialog(state),
    currentVersion: getCurrentVersion(state),
});

const mapDispatchToProps = (dispatch: ThunkDispatch<Dispatch, State, any>): ReleaseNotesDispatchProps => ({
    setShowReleaseNotes: (payload): void => {
        dispatch(serviceInformationActions.setShowReleaseNotes(payload));
    },
    toggleGlossaryDialog: (): void => {
        dispatch(serviceInformationActions.toggleGlossaryDialog());
    },
});

export const ReleaseNotesContainer = connect(mapStateToProps, mapDispatchToProps)(ReleaseNotes);
